import React, { useEffect, useState, useRef } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, Scissors, Loader2, Check, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const CuttingStation = () => {
  const navigate = useNavigate();
  const [slips, setSlips] = useState([]);
  const [slipId, setSlipId] = useState('');
  const [session, setSession] = useState(null);
  const [usnInput, setUsnInput] = useState('');
  const [cuts, setCuts] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [printing, setPrinting] = useState(false);
  const usnRef = useRef(null);

  useEffect(() => {
    apiClient.get('/sample/pick-slips?sample_type=SWATCH').then((r) => {
      // Only PICKED / COMPLETED / PARTIAL ones are eligible
      const ok = (r.data.slips || []).filter(
        (s) => ['PICKED', 'COMPLETED', 'PARTIALLY_PICKED'].includes(s.status),
      );
      setSlips(ok);
    });
  }, []);

  useEffect(() => { if (session && usnRef.current) usnRef.current.focus(); }, [session]);

  const startSession = async () => {
    if (!slipId) { toast.error('Pick a slip first'); return; }
    try {
      const r = await apiClient.post('/sample/cutting/start-session', { slip_id: slipId });
      setSession(r.data);
      setCuts([]);
      toast.success(`Cutting session ${r.data.session_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const scan = async (e) => {
    e?.preventDefault?.();
    const u = usnInput.trim();
    if (!u || !session) return;
    setScanning(true);
    try {
      const r = await apiClient.post('/sample/cutting/scan-usn', {
        session_id: session.session_id, usn_code: u,
      });
      if (r.data.duplicate) {
        toast.warning(`${u} already cut earlier`);
      } else {
        setCuts((c) => [...c, r.data]);
        toast.success(`${u} → 4 swatches`);
      }
      setUsnInput('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Scan failed'); }
    finally { setScanning(false); }
  };

  const printAll = async () => {
    if (!cuts.length) return;
    setPrinting(true);
    try {
      const r = await apiClient.post('/sample/swatch-labels/render', {
        parent_usns: cuts.map((c) => c.parent_usn),
      });
      toast.success(`Rendered ${r.data.count} labels — sending to printer…`);
      // Write to print preview window
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(`<html><head><title>Swatch Labels</title>
          <style>
            body{font-family:monospace;padding:20px}
            .lbl{border:1px solid #000;padding:10px;margin-bottom:10px;page-break-inside:avoid;width:300px}
            .usn{font-size:18px;font-weight:bold}
            .meta{font-size:12px;color:#444}
            .price{font-size:14px;font-weight:bold;color:#000;margin-top:4px}
          </style></head><body>`);
        r.data.labels.forEach((l) => {
          w.document.write(`<div class="lbl">
            <div class="usn">${l.usn_code}</div>
            <div class="meta">${l.fg_barcode || ''} · ${l.design_name || ''}</div>
            <div class="meta">${l.shade || ''} · ${l.size || ''}</div>
            <div class="price">MRP: ${l.mrp_formatted || '-'} &nbsp; WSP: ${l.wsp_formatted || '-'}</div>
            <div class="meta">parent: ${l.parent_usn}</div>
          </div>`);
        });
        w.document.write('</body></html>');
        w.document.close();
        w.focus();
        setTimeout(() => w.print(), 300);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Print failed'); }
    finally { setPrinting(false); }
  };

  const finalize = async () => {
    if (!session) return;
    try {
      await apiClient.post(`/sample/cutting/finalize/${session.session_id}`);
      toast.success('Cutting session closed');
      setSession(null); setCuts([]); setSlipId('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <StaffScanGate stationName="Sample — Cutting Station">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="cutting-station">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/sample')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Cutting Station — Swatch Cards</h1>
              <p className="text-sm text-[#E8DCC8]">1 piece → 4 swatch cards (USN derived)</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {!session ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pick a SWATCH slip to start cutting</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-2 items-end">
              <div className="flex-1 max-w-md">
                <Select value={slipId} onValueChange={setSlipId}>
                  <SelectTrigger data-testid="slip-select-trigger">
                    <SelectValue placeholder="Select a swatch slip…" />
                  </SelectTrigger>
                  <SelectContent>
                    {slips.length === 0 && <SelectItem disabled value="none">No slips ready for cutting</SelectItem>}
                    {slips.map((s) => (
                      <SelectItem key={s.slip_id} value={s.slip_id}>
                        {s.pick_slip_number} — {s.total_qty} pcs ({s.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={startSession} disabled={!slipId}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="start-session-btn">
                <Scissors className="w-4 h-4 mr-2" /> Start Cutting
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{session.session_id}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={scan} className="flex gap-2">
                  <Scan className="w-4 h-4 text-gray-500 mt-2" />
                  <Input ref={usnRef} value={usnInput}
                    onChange={(e) => setUsnInput(e.target.value)}
                    placeholder="Scan original USN…"
                    className="h-10 max-w-md" disabled={scanning}
                    data-testid="usn-scan-input" />
                  <Button type="submit" disabled={scanning} data-testid="usn-scan-submit">
                    {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cut'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Cuts ({cuts.length})</CardTitle>
                  <div className="flex gap-2">
                    <Button onClick={printAll} disabled={!cuts.length || printing}
                      data-testid="print-labels-btn">
                      {printing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                      Print {cuts.length * 4} Labels
                    </Button>
                    <Button variant="outline" onClick={finalize} data-testid="finalize-session-btn">
                      <Check className="w-4 h-4 mr-2" /> Close Session
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parent USN</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Swatch USNs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!cuts.length && (
                      <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-6">
                        Scan a USN above to begin
                      </TableCell></TableRow>
                    )}
                    {cuts.map((c) => (
                      <TableRow key={c.parent_usn} data-testid={`cut-row-${c.parent_usn}`}>
                        <TableCell className="font-mono text-xs">{c.parent_usn}</TableCell>
                        <TableCell className="text-xs">{c.fg?.fg_barcode || '—'}</TableCell>
                        <TableCell className="text-xs">{c.fg?.design_name || '—'}</TableCell>
                        <TableCell className="font-mono text-[10px]">
                          {c.swatch_usns.map((s) => (
                            <Badge key={s} variant="outline" className="mr-1 text-[10px]">{s}</Badge>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
    </StaffScanGate>
  );
};

export default CuttingStation;
